import React, { useState, useEffect, useCallback } from 'react';
import AdminLayout from '../../layouts/AdminLayout';
import { getChatConversations } from '../../services/messageService';
import MessageChatDialog from './MessageChatDialog';
import Pagination from '../../components/Pagination';
import {
  Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  Button, Box, Typography, CircularProgress, Snackbar, Chip, IconButton
} from '@material-ui/core';
import {  makeStyles , useTheme } from "@material-ui/core/styles";
import ChatIcon from '@material-ui/icons/Chat';

const useStyles = makeStyles((theme) => ({
  tableContainer: {
    borderRadius: 16,
    boxShadow: '0 4px 24px rgba(0,0,0,0.2)',
    overflow: 'hidden',
    border: `1px solid ${theme.palette.divider}`,
  },
  loadingContainer: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: 400,
  },
  actionBtn: {
    color: theme.palette.text.secondary,
    padding: 8,
    '&:hover': { backgroundColor: theme.palette.action.hover, color: theme.palette.text.primary },
  },
  countChip: {
    background: 'linear-gradient(135deg, rgba(16,185,129,0.15) 0%, rgba(5,150,105,0.15) 100%)',
    color: '#10b981',
    border: '1px solid rgba(16,185,129,0.3)',
    fontWeight: 700,
    fontSize: '0.85rem',
    height: 36,
    padding: '0 8px',
    borderRadius: 10,
  },
}));

export default function MessageListPage() {
  const classes = useStyles();
  
  const theme = useTheme();
  const [conversations, setConversations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [selectedConv, setSelectedConv] = useState(null);
  const [chatOpen, setChatOpen] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '' });

  const pageSize = 20;

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await getChatConversations();
      setConversations(data);
    } catch (e) {
      setSnackbar({ open: true, message: 'Failed to load conversations' });
    }
    setLoading(false);
  }, []);

  useEffect(() => { loadData(); }, [loadData]);

  const handleOpenChat = (conv) => {
    setSelectedConv(conv);
    setChatOpen(true);
  };

  const formatDate = (ts) => {
    if (!ts) return '';
    const d = ts.toDate ? ts.toDate() : new Date(ts);
    const now = new Date();
    const diff = now - d;
    if (diff < 86400000) return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    return d.toLocaleDateString();
  };

  const totalPages = Math.max(1, Math.ceil(conversations.length / pageSize));
  const paginatedConversations = conversations.slice((page - 1) * pageSize, page * pageSize);

  return (
    <AdminLayout title="User Messages">
      <Box mb={3} display="flex" justifyContent="space-between" alignItems="center" flexWrap="wrap" gap={2}>
        <Typography variant="body2" color="textSecondary">
          Live support chats and user messages.
        </Typography>
        <Box display="flex" style={{ gap: 16 }} alignItems="center">
          <Chip label={`Total Conversations: ${conversations.length}`} className={classes.countChip} />
          <Button variant="outlined" onClick={loadData} color="default">
            Refresh
          </Button>
        </Box>
      </Box>

      {loading ? (
        <div className={classes.loadingContainer}>
          <CircularProgress size={40} />
          <Typography color="textSecondary" style={{ marginTop: 16 }}>Loading conversations...</Typography>
        </div>
      ) : (
        <TableContainer component={Paper} className={classes.tableContainer} elevation={0}>
          <Table>
            <TableHead>
              <TableRow>
                <TableCell>User</TableCell>
                <TableCell>Last Message</TableCell>
                <TableCell>Status</TableCell>
                <TableCell>Last Activity</TableCell>
                <TableCell align="right">Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {conversations.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={5} align="center">
                    <Typography style={{ color: theme.palette.text.secondary, padding: 24 }}>No conversations yet.</Typography>
                  </TableCell>
                </TableRow>
              ) : (
                paginatedConversations.map((conv) => (
                  <TableRow key={conv.id} hover>
                    <TableCell>
                      <Typography color="textPrimary" style={{ fontSize: '0.9rem' }}>
                        {conv.userName || 'Unknown'}
                      </Typography>
                      <Typography style={{ color: theme.palette.text.secondary, fontSize: '0.75rem' }}>
                        {conv.userEmail}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography color="textSecondary" style={{ fontSize: '0.85rem', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {conv.lastMessage || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      {conv.unreadByAdmin ? (
                        <Chip
                          label="Unread"
                          size="small"
                          style={{ backgroundColor: 'rgba(239,68,68,0.15)', color: '#ef4444', fontWeight: 600 }}
                        />
                      ) : (
                        <Chip
                          label="Read"
                          size="small"
                          style={{ backgroundColor: theme.palette.action.hover, color: theme.palette.text.secondary, fontWeight: 600 }}
                        />
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography color="textSecondary" style={{ fontSize: '0.85rem' }}>
                        {formatDate(conv.lastMessageAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <IconButton className={classes.actionBtn} onClick={() => handleOpenChat(conv)}>
                        <ChatIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      )}

      {!loading && conversations.length > 0 && (
        <Pagination
          page={page}
          totalPages={totalPages}
          totalCount={conversations.length}
          pageSize={pageSize}
          onPageChange={setPage}
        />
      )}

      {selectedConv && (
        <MessageChatDialog
          open={chatOpen}
          conversation={selectedConv}
          onClose={() => { setChatOpen(false); loadData(); }}
        />
      )}

      <Snackbar
        open={snackbar.open}
        message={snackbar.message}
        autoHideDuration={4000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </AdminLayout>
  );
}
